import React, { useState, useCallback, useEffect } from 'react';
import Udesk from 'Udesk';
import { FormBuilder, Space, Select, TimePicker } from 'udesk-ui';
import moment from 'moment';
import UdeskLocales from 'UdeskLocales';
import type { FormProps } from 'udesk-ui/lib/form-builder/types/form';
import styled from 'styled-components';
import { CustomFilter } from '../business-summary/components/CustomFilter';

const MONTH_DAYS = Array.from({ length: 30 }, (_, i) => i + 1);
// const Hours = Array.from(Array(24), (_, k) => k);
// const getFiltedHours = () => Hours.filter((i) => !(i > 1 && i < 8));
const selectStyles: React.CSSProperties = { width: 200 };

const Wrapper = styled.div`
    margin-bottom: 20px;
`;

/** 推送时机 */
export const VIEW_GRADE_PUSH_TIMING = {
    REAL_TIME: 'realtime',
    SCHEDULED: 'scheduled',
} as const;

/** 单条查看规则：筛选条件 + 定时策略 */
export interface ViewGradeRule {
    _id?: string;
    filter?: {
        conditionList?: any[];
        judgeStrategy?: number;
        customJudgeLogic?: string;
    };
    strategy?: {
        loadCycle?: number;
        dayOfWeek?: number;
        dayOfMonth?: number;
        executeTime?: string;
        loadScope?: number;
    };
}

export interface ViewGradeValue {
    enabled?: boolean;
    pushTiming?: 'realtime' | 'scheduled';
    scheduledRules?: ViewGradeRule[];
}

const defaultRule = (): ViewGradeRule => {
    const weekDays = Udesk.enums.samplingFrequencyWeekDays || [];
    const dayTypes = Udesk.enums.samplingEveryDayTimeTypes || [];
    const todayScope = (Array.isArray(dayTypes) && dayTypes.find((t: any) => t.key === 'today')) || dayTypes[0];
    return {
        _id: `rule_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        filter: {
            conditionList: [],
            judgeStrategy: 1,
            customJudgeLogic: '',
        },
        strategy: {
            loadCycle: Udesk.enums.samplingFrequencyTypes.everyDay.id,
            dayOfWeek: weekDays[0]?.id ?? 1,
            dayOfMonth: 1,
            executeTime: '09:00:00',
            loadScope: todayScope?.id ?? 2,
        },
    };
};

interface ViewGradeProps {
    value?: ViewGradeValue;
    onChange?: (v: ViewGradeValue) => void;
    disabled?: boolean;
    type?: number;
    taskName?: string;
    customName?: string;
    customId?: number;
}

/** 添加频率组件，与 auto-add-component-mixed 的 AddFrequency 一致 */
const AddFrequency = React.memo((props: any) => {
    const locales = UdeskLocales['current'];
    const { value = {}, onChange, onAddFrequencyLoadCycleChange, disabled } = props;
    const { loadCycle, dayOfWeek, dayOfMonth, executeTime } = value || {};

    const onSelectChange = (type: string, v: any) => {
        const newValue = { ...value, [type]: v };
        onChange(newValue);
        onAddFrequencyLoadCycleChange?.(newValue);
    };
    const onTimePickerChange = (_time: any, timeString: string) => {
        const newValue = { ...value, executeTime: timeString + ':00' };
        onChange(newValue);
    };
    return (
        <Space>
            <Select
                value={loadCycle ?? Udesk.enums.samplingFrequencyTypes.everyDay.id}
                onChange={onSelectChange.bind(null, 'loadCycle')}
                style={selectStyles}
                disabled={disabled}
            >
                {Udesk.enums.samplingFrequencyTypes.map((item: any) => (
                    <Select.Option key={item.id} value={item.id}>
                        {item.name}
                    </Select.Option>
                ))}
            </Select>
            {loadCycle === Udesk.enums.samplingFrequencyTypes.everyWeek.id && (
                <Select
                    value={dayOfWeek ?? Udesk.enums.samplingFrequencyWeekDays?.Monday?.id ?? (Udesk.enums.samplingFrequencyWeekDays || [])[0]?.id}
                    onChange={onSelectChange.bind(null, 'dayOfWeek')}
                    style={selectStyles}
                    disabled={disabled}
                >
                    {(Udesk.enums.samplingFrequencyWeekDays || []).map((item: any) => (
                        <Select.Option key={item.id} value={item.id}>
                            {item.name}
                        </Select.Option>
                    ))}
                </Select>
            )}
            {loadCycle === Udesk.enums.samplingFrequencyTypes.everyMonth.id && (
                <Select
                    value={dayOfMonth ?? 1}
                    onChange={onSelectChange.bind(null, 'dayOfMonth')}
                    style={selectStyles}
                    disabled={disabled}
                >
                    {MONTH_DAYS.map((item) => (
                        <Select.Option key={item} value={item}>
                            {`${item}${locales?.components?.pushRulesGather?.dateUnit ?? /* 号 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.number}`}
                        </Select.Option>
                    ))}
                </Select>
            )}
            <TimePicker
                // disabledHours={getFiltedHours}
                defaultValue={
                    executeTime
                        ? moment(executeTime, ['HH:mm', 'HH:mm:'])
                        : moment('09:00', 'HH:mm')
                }
                format="HH:mm"
                allowClear={false}
                onChange={onTimePickerChange}
                showNow={false}
                disabled={disabled}
            />
        </Space>
    );
});

const ViewGrade: React.FC<ViewGradeProps> = (props) => {
    const { value: propsValue, onChange: propsOnChange, disabled, type } = props;
    const locales = UdeskLocales['current'];

    const [wrapForm] = FormBuilder.useForm();
    const [loadScopeOptions, setLoadScopeOptions] = useState<{ id: number; name: string | (() => string) }[]>(
        (Udesk.enums.samplingEveryDayTimeTypes || []).filter((i: any) => i.id > 1)
    );
    const [initialValues, setInitialValues] = useState<{
        enabled?: boolean;
        pushTiming?: string;
        addFrequency?: ViewGradeRule['strategy'];
        loadScope?: number;
    }>({
        enabled: true,
        pushTiming: VIEW_GRADE_PUSH_TIMING.REAL_TIME,
        addFrequency: undefined,
        loadScope: undefined,
    });

    const rule = useCallback(() => {
        const rules = propsValue?.scheduledRules;
        return Array.isArray(rules) && rules.length > 0 ? rules[0] : null;
    }, [propsValue?.scheduledRules])();

    const onAddFrequencyLoadCycleChange = useCallback(({ loadCycle }: { loadCycle?: number }) => {
        if (loadCycle === Udesk.enums.samplingFrequencyTypes.everyWeek.id) {
            setLoadScopeOptions((Udesk.enums.samplingEveryWeekTimeTypes || []).filter((i: any) => i.id > 1));
        } else if (loadCycle === Udesk.enums.samplingFrequencyTypes.everyMonth.id) {
            setLoadScopeOptions((Udesk.enums.samplingEveryMonthTimeTypes || []).filter((i: any) => i.id > 1));
        } else if (loadCycle === Udesk.enums.samplingFrequencyTypes.everyDay.id) {
            setLoadScopeOptions((Udesk.enums.samplingEveryDayTimeTypes || []).filter((i: any) => i.id > 1));
        }
    }, []);

    useEffect(() => {
        const enabled = propsValue?.enabled !== false;
        const pushTiming = propsValue?.pushTiming ?? VIEW_GRADE_PUSH_TIMING.REAL_TIME;
        const currentRule = rule ?? defaultRule();
        const strategy = currentRule.strategy;
        setInitialValues({
            enabled,
            pushTiming,
            addFrequency: strategy
                ? {
                    loadCycle: strategy.loadCycle,
                    dayOfWeek: strategy.dayOfWeek,
                    dayOfMonth: strategy.dayOfMonth,
                    executeTime: strategy.executeTime,
                }
                : undefined,
            loadScope: strategy?.loadScope ?? Udesk.enums.samplingEveryDayTimeTypes?.today?.id ?? 2,
        });
        onAddFrequencyLoadCycleChange(strategy ?? { loadCycle: Udesk.enums.samplingFrequencyTypes.everyDay.id });
    }, [propsValue?.enabled, propsValue?.pushTiming, propsValue?.scheduledRules]);

    useEffect(() => {
        wrapForm.resetFields();
    }, [initialValues, wrapForm]);

    const onValuesChange = useCallback(
        (_: any, values: any) => {
            const { pushTiming, addFrequency, loadScope } = values;
            const baseRule = rule ?? defaultRule();
            const strategy =
                pushTiming === VIEW_GRADE_PUSH_TIMING.SCHEDULED
                    ? { ...(baseRule.strategy ?? {}), ...addFrequency, loadScope }
                    : baseRule.strategy;
            const nextRule: ViewGradeRule = {
                ...baseRule,
                filter: baseRule.filter,
                strategy,
            };
            propsOnChange?.({
                ...propsValue,
                enabled: propsValue?.enabled !== false,
                pushTiming: pushTiming ?? VIEW_GRADE_PUSH_TIMING.REAL_TIME,
                scheduledRules: pushTiming === VIEW_GRADE_PUSH_TIMING.SCHEDULED ? [nextRule] : [],
            });
        },
        [rule, propsValue, propsOnChange]
    );

    const updateRule = useCallback(
        (patch: Partial<ViewGradeRule>) => {
            const current = rule ?? defaultRule();
            const next = { ...current };
            if (patch.filter !== undefined) next.filter = { ...current.filter, ...patch.filter };
            if (patch.strategy !== undefined) next.strategy = { ...current.strategy, ...patch.strategy };
            if (patch._id !== undefined) next._id = patch._id;
            propsOnChange?.({
                ...propsValue,
                enabled: propsValue?.enabled !== false,
                pushTiming: propsValue?.pushTiming ?? VIEW_GRADE_PUSH_TIMING.REAL_TIME,
                scheduledRules: [next],
            });
        },
        [rule, propsValue, propsOnChange]
    );

    const isScheduled = (propsValue?.pushTiming ?? VIEW_GRADE_PUSH_TIMING.REAL_TIME) === VIEW_GRADE_PUSH_TIMING.SCHEDULED;

    const config: FormProps = {
        initialValues,
        onValuesChange,
        customComponents: { AddFrequency },
        fields: [
            {
                name: 'pushTiming',
                label: locales?.pages?.tasks?.manage?.template?.automaticAdd?.index?.autoAdd?.autoAddType ?? /* 推送时机 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.pushTiming,
                type: 'RadioGroup',
                props: {
                    optionType: 'button',
                    buttonStyle: 'solid',
                    options: [
                        { label: /* 实时查看 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.realtimeViewing, value: VIEW_GRADE_PUSH_TIMING.REAL_TIME },
                        { label: /* 定时查看 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.timingView, value: VIEW_GRADE_PUSH_TIMING.SCHEDULED },
                    ],
                    disabled,
                },
                rules: [{ required: true }],
                effect: {
                    visible: {
                        [VIEW_GRADE_PUSH_TIMING.SCHEDULED]: ['addFrequency', 'loadScope'],
                    },
                },
            },
            {
                name: 'addFrequency',
                label: locales?.pages?.tasks?.manage?.template?.automaticAdd?.index?.autoAdd?.addFrequency ?? /* 添加频率 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.addFrequency,
                type: 'AddFrequency',
                props: {
                    onAddFrequencyLoadCycleChange,
                    disabled,
                },
                rules: [{ required: isScheduled }],
            },
            {
                name: 'loadScope',
                label: locales?.pages?.tasks?.manage?.template?.automaticAdd?.index?.autoAdd?.add ?? /* 时间范围 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.timeRange,
                type: 'Select',
                props: {
                    options: loadScopeOptions.map((i: any) => ({
                        label: typeof i.name === 'function' ? i.name() : i.name,
                        value: i.id,
                    })),
                    style: selectStyles,
                    placeholder: locales?.labels?.pleaseSelect ?? /* 请选择 */UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.pleaseSelect,
                    disabled,
                },
                rules: [{ required: isScheduled }],
            },
        ],
        footerRender: false,
        labelCol: { span: 0 },
        wrapperCol: { span: 24 },
    };

    return (
        <Wrapper>
            <FormBuilder {...config} form={wrapForm} />
            {isScheduled && (
                <>
                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                        <span className="automatic-content-item-body-item-title" style={{ marginRight: 8 }}>{/* 筛选条件： */}{UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.components.viewGrade.index.filterCriteria}</span>
                    </div>
                    <CustomFilter
                        type={type}
                        value={rule?.filter}
                        onChange={(filter) => updateRule({ filter })}
                        wrapperStyle={{ marginBottom: 12 }}
                    />
                </>
            )}
        </Wrapper>
    );
};

export default React.memo(ViewGrade);
